import VdpPhotoSlideshowChangeEvent, {
  Directions as EventDirections,
} from 'lib/Tracking/Vdp/PhotoSlideshow/ChangeEvent';
import VdpPhotoSlideshowCloseEvent from 'lib/Tracking/Vdp/PhotoSlideshow/CloseEvent';
import useEvents from 'lib/hooks/useEvents';
import { Carousel } from 'react-responsive-carousel';
import { SyntheticEvent, useEffect, useContext } from 'react';
import viewEvent from 'lib/Tracking/Vdp/PhotoSlideshow/ModalViewEvent';
import closeEvent from 'lib/Tracking/Vdp/PhotoSlideshow/ModalCloseEvent';
import css from 'styled-jsx/css';
import { useTranslation } from 'react-i18next';
import { screen } from 'components/styles/variables';
import { Arrow, Directions } from 'components/Shared/Arrow';
import { useKey } from 'react-use';
import VdpEventContext from 'contexts/VdpEventContext';
import { LEADERBOARD, MEDIUM_RECTANGLE, TINY_RECTANGLE } from 'lib/GoogleAds/Sizes';
import ConditionalRefreshAd from 'components/Ad/ConditionalRefreshAd';
import { LARGE, X_SMALL } from 'lib/DeviceScreen';
import useRefresh from 'lib/hooks/useVdpPhotoLightboxRefreshableAds';
import Counter from 'components/Shared/Counter';
import Modal from './Modal';

interface ContentProps {
  imageUrls: string[];
  initialSlide: number;
  onClose: () => void;
  dealerRemoteId?: string;
  onSlideChange: (num: number) => void;
}

const leftAdCss = css.resolve`
  .left-ad {
    display: none;
  }

  @media (min-width: ${screen.smMin}) {
    .left-ad {
      grid-area: left-ad;
      align-self: start;
      display: block;
      height: 600px;
      width: 300px;
    }
  }
`;

const bottomAdCss = css.resolve`
  .bottom-ad {
    display: none;
  }

  @media (min-width: ${screen.smMin}) {
    .bottom-ad {
      margin-bottom: 29px;
      margin-left: auto;
      margin-right: auto;
      left: 0;
      right: 0;
      width: 728px;
      height: 90px;
      position: absolute;
      bottom: 0;
      display: block;
    }
  }
`;

const mobileAdCss = css.resolve`
  .mobile-ad {
    position: fixed;
    bottom: 12px;
    width: 100%;
  }
`;

const counterCss = css.resolve`
  @media (min-width: ${screen.smMin}) {
    .counter {
      margin: 0px 16px;
    }
  }
`;

const sliderCss = css.resolve`
  @media (min-width: ${screen.smMin}) {
    .slider :global(.carousel-slider .slider-wrapper .slide) {
      background: transparent;
    }

    .slider :global(.carousel-slider .slider-wrapper .selected img) {
      width: auto;
    }
  }
`;

export function Content({
  imageUrls,
  initialSlide,
  onClose,
  dealerRemoteId,
  onSlideChange,
}: ContentProps): JSX.Element {
  const logger = useEvents();

  function logSlideshowChangeEvent(direction: EventDirections): void {
    const event = new VdpPhotoSlideshowChangeEvent(direction);

    logger.log(event);
  }

  function next(): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onSlideChange((currSlide: number) => (currSlide + 1) % imageUrls.length);

    logSlideshowChangeEvent('fwd');
  }

  function previous(): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onSlideChange((currSlide: number) => (currSlide - 1) % imageUrls.length);

    logSlideshowChangeEvent('bk');
  }

  const { t } = useTranslation();
  useKey('ArrowRight', next);
  useKey('ArrowLeft', previous);

  const { trackingParams } = useContext(VdpEventContext);
  useEffect(() => {
    logger.log(viewEvent({ trackingParams }));
  }, [logger, trackingParams]);

  const { hasChangedPhoto, hasFiveSecondsPassed, onRefresh, registerAd, refreshAdThrottled } =
    useRefresh();

  return (
    <button className="lightbox" data-testid="lightbox" onClick={(): void => onClose()}>
      <div className="left">
        <ConditionalRefreshAd
          adConfig={{
            targeting: {
              inLine: 2,
              sellerId: dealerRemoteId,
            },
            dimensions: [
              [300, 250],
              [300, 600],
            ],
          }}
          placeholderSize={MEDIUM_RECTANGLE}
          shouldRefresh={hasChangedPhoto && hasFiveSecondsPassed}
          onRefresh={onRefresh}
          isCollapsible={false}
          registerAd={registerAd}
        />
      </div>
      <div className="right">
        <div className="flex-container">
          <div className="carousel-container">
            <Carousel
              selectedItem={initialSlide}
              onChange={onSlideChange}
              showArrows={false}
              transitionTime={300}
              showIndicators={false}
              showStatus={false}
              showThumbs={false}
              className={`slider ${sliderCss.className}`}
              onSwipeStart={refreshAdThrottled}
            >
              {imageUrls.map((image, index) => (
                <img
                  className="image-slide"
                  src={image}
                  key={image}
                  alt={`Image ${index + 1}`}
                  onClick={(e: SyntheticEvent): void => e.stopPropagation()}
                />
              ))}
            </Carousel>
          </div>
          <div className="image-controls">
            <Arrow
              className="transparent-hover"
              onClick={(e): void => {
                e.stopPropagation();
                previous();
                refreshAdThrottled();
              }}
              direction={Directions.Left}
              alt={t('previous')}
            />
            <Counter
              className={`counter ${counterCss.className}`}
              count={imageUrls.length}
              index={initialSlide}
            />
            <Arrow
              className="transparent-hover"
              onClick={(e): void => {
                e.stopPropagation();
                next();
                refreshAdThrottled();
              }}
              direction={Directions.Right}
              alt={t('next')}
            />
          </div>
        </div>
        <ConditionalRefreshAd
          adConfig={{
            dimensions: [[728, 90]],
            targeting: { inLine: 9, sellerId: dealerRemoteId },
            screenSizes: [LARGE],
          }}
          placeholderSize={LEADERBOARD}
          shouldRefresh={hasChangedPhoto && hasFiveSecondsPassed}
          onRefresh={onRefresh}
          isCollapsible={false}
          registerAd={registerAd}
        />
      </div>
      <ConditionalRefreshAd
        adConfig={{
          targeting: { inLine: 11, sellerId: dealerRemoteId },
          dimensions: [
            [320, 50],
            [320, 80],
          ],
          screenSizes: [X_SMALL],
        }}
        placeholderSize={TINY_RECTANGLE}
        shouldRefresh={hasChangedPhoto && hasFiveSecondsPassed}
        onRefresh={onRefresh}
        registerAd={registerAd}
        className={`mobile-ad ${mobileAdCss.className}`}
      />
      {leftAdCss.styles}
      {bottomAdCss.styles}
      {counterCss.styles}
      {sliderCss.styles}
      {mobileAdCss.styles}
      <style jsx>{`
        .left {
          display: none;
        }

        @media (min-width: ${screen.lgMin}) {
          .left {
            display: block;
            padding: 56px 28px 0px 28px;
          }
        }

        .right {
          position: relative;
        }

        @media (min-width: ${screen.smMin}) {
          .right {
            display: flex;
            flex-direction: column;
            width: 100%;
            padding-top: 56px;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .right {
            padding-right: 28px;
          }
        }

        .flex-container {
          width: 100%;
          max-height: calc(100vh - 185px);
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        @media (min-width: ${screen.smMin}) {
          .flex-container {
            padding-top: 16px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .flex-container {
            padding: 0px 16px;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .flex-container {
            padding: 0px;
          }
        }

        .carousel-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .image-controls {
          display: flex;
          position: relative;
          width: 100%;
          height: 77px;
          box-sizing: border-box;
          align-items: center;
          justify-content: center;
        }

        .image-slide {
          height: 100%;
          width: 100%;
          max-height: calc(100vh - 268px);
          object-fit: contain;
          vertical-align: middle;
          pointer-events: all;
        }

        .arrows {
          position: absolute;
          right: 10px;
          top: 1px;
          display: flex;
        }

        .lightbox {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          position: relative;
          background: transparent;
          border: none;
          padding: 0;
        }

        @media (min-width: ${screen.smMin}) {
          .lightbox {
            height: 100%;
            width: 100%;
            display: flex;
            flex-direction: row;
            background: transparent;
            border: none;
            padding: 0;
          }
        }
      `}</style>
    </button>
  );
}

interface Props {
  imageUrls: string[];
  initialSlide: number;
  isOpen: boolean;
  onClose(): void;
  dealerRemoteId?: string;
  onSlideChange: (num: number) => void;
}

export default function PhotoLightbox({
  imageUrls,
  initialSlide,
  isOpen,
  onClose,
  dealerRemoteId,
  onSlideChange,
}: Props): JSX.Element {
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);

  function trackPhotoSlideshowClose(): void {
    logger.log(VdpPhotoSlideshowCloseEvent({ trackingParams }));
    logger.log(closeEvent({ trackingParams }));
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={(): void => {
        trackPhotoSlideshowClose();
        onClose();
      }}
    >
      <button className="content-container" onClick={onClose}>
        <Content
          imageUrls={imageUrls}
          initialSlide={initialSlide}
          onClose={onClose}
          dealerRemoteId={dealerRemoteId}
          onSlideChange={onSlideChange}
        />
      </button>

      <style jsx>{`
        .content-container {
          width: 100%;
          height: 100%;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          background: transparent;
          border: none;
          padding: 0;
        }
      `}</style>
    </Modal>
  );
}
